import { useMemo, useState } from 'react';
import Header from '../../../header';
import { useAuth } from '../../../contexts/AuthContext';
import { apiPost } from '../../../utils/api';
import { EhrProvider, Organization } from '../../../shared/types';
import { Card } from '../../Card';
import { StaticDataTable } from '../../table/StaticDataTable';
import { StaticDataColumnProps, StaticDataRow } from '../../table/StaticDataTable';
import { Roles } from 'src/shared/abilities';

const OrganizationPage = () => {
  const { user, organizations, setStorage } = useAuth();
  const isMasterAdmin = user?.master_admin === true;
  const [ mode, setMode ] = useState<'select' | 'create'>('select');
  const [ newOrgName, setNewOrgName ] = useState('');
  const [ adminEmail, setAdminEmail ] = useState('');
  const [ selectedProvider, setSelectedProvider ] = useState<EhrProvider>('epic');
  const [ error, setError ] = useState('');
  const [ creating, setCreating ] = useState(false);

  const handleCreateOrg = async () => {
    try {
      if (newOrgName.trim() && adminEmail.trim()) {
        setCreating(true);
        setError('');
        const response = await apiPost(`/api/organizations`, {
          name: newOrgName.trim(),
          ehr_provider: selectedProvider,
          admin_email: adminEmail.trim(),
        });
        const data = await response.json();

        if (!data.error) {
          setStorage({
            user: data.user,
            organizations: data.organizations,
            orgMemberships: data.orgMemberships,
            isDemoSession: data.isDemoSession,
          });
          window.location.href = `/${data.organization.id}/general-settings`;
        } else {
          setError(data.error);
          setCreating(false);
        }
      }
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization. Please try again.');
      setCreating(false);
    }
  }

  const columns: StaticDataColumnProps[] = useMemo(() => [
    { title: "ID", sortable: true },
    { title: "Name", sortable: true, defaultSort: true },
    { title: "Provider", sortable: true },
    { title: "Admin Status", sortable: true },
  ], []);

  const orgRows: StaticDataRow[][] = useMemo(() => {
    return organizations.map((org: Organization) => [
      {
        name: "Key",
        value: org.id + org.name,
      },
      {
        name: "ID",
        value: org.id,
        element: (
          <span className="text-sm text-gray-900">{org.id}</span>
        )
      },
      {
        name: "Name",
        value: org.name,
        element: (
          <button className="text-md font-semibold text-brand-600 cursor-pointer hover:underline" onClick={(e) => {
            e.stopPropagation();
            if (org.id) window.location.href = `/${org.id}/general-settings`;
          }}>
            {org.name}
          </button>
        ),
      },
      {
        name: "Provider",
        value: org.ehr_provider,
        element: (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            org.ehr_provider.toLowerCase() === 'epic'
              ? 'bg-red-100 text-red-800'
              : 'bg-green-100 text-green-800'
          }`}>
            {org.ehr_provider}
          </span>
        ),
      },
      {
        name: "Admin Status",
        value: org.user_role,
        element: (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            org.user_role === Roles.MASTER_ADMIN
              ? 'bg-brand-100 text-brand-800'
              : org.user_role === Roles.ORG_ADMIN
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800'
          }`}>
            {org.user_role === Roles.MASTER_ADMIN ? 'Master Admin' : org.user_role === Roles.ORG_ADMIN ? 'Admin' : 'User'}
          </span>
        ),
      },
    ]);
  }, [organizations])

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="w-full p-8">
          <h1 className="text-3xl font-bold mb-4 text-center text-gray-800">Organization</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto text-center mb-8">
            {isMasterAdmin
              ? 'Select an existing organization or create a new one to get started'
              : 'Select an organization to get started'}
          </p>

          {isMasterAdmin && (
            <div className="flex justify-center mb-8">
              <div className="bg-gray-100 rounded-lg p-1 inline-flex">
                <button
                  onClick={() => setMode('select')}
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${
                    mode === 'select'
                      ? 'bg-white text-brand-600 shadow'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Select Existing
                </button>
                <button
                  onClick={() => setMode('create')}
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${
                    mode === 'create'
                      ? 'bg-white text-brand-600 shadow'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Create new Organization
                </button>
              </div>
            </div>
          )}

          {mode === 'select' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Select Organization</h2>
              <div className="space-y-6">
                {organizations && organizations.length > 0 ? (
                  <StaticDataTable
                    columns={columns}
                    headerClass="bg-gray-50"
                    headerColor={false}
                    tableId="organizations"
                    rows={orgRows}
                    rowId={row => row.Key}
                  />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-lg">No organizations available</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {mode === 'create' && isMasterAdmin && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Create Organization</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter organization name"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Admin Email
                  </label>
                  <input
                    type="email"
                    placeholder="admin@organization.com"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    An invitation will be sent to this email. The email domain will be set as the allowed domain for this organization.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Provider System
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="provider"
                        value="epic"
                        checked={selectedProvider === 'epic'}
                        onChange={(e) => setSelectedProvider(e.target.value as EhrProvider)}
                        className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-gray-700">Epic</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="provider"
                        value="cerner"
                        checked={selectedProvider === 'cerner'}
                        onChange={(e) => setSelectedProvider(e.target.value as EhrProvider)}
                        className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-gray-700">Cerner</span>
                    </label>
                  </div>
                </div>
                <button
                  onClick={handleCreateOrg}
                  disabled={!newOrgName.trim() || !adminEmail.trim() || creating}
                  className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                >
                  <span className="flex items-center justify-center gap-2">
                    {creating && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {creating ? 'Creating...' : 'Create Organization'}
                  </span>
                </button>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OrganizationPage;
